<script setup lang="ts">
import type { ContentBlockRendererProps, ContentBlockType } from '@nimbus/types'
import { computed, type defineAsyncComponent, filterRenderableBlocks } from '#imports'
import {
    LazyNimbusContentBlockText,
    LazyNimbusContentBlockLink,
    LazyNimbusContentBlockList,
    LazyNimbusContentBlockListItem,
    LazyNimbusContentBlockHeading,
    LazyNimbusContentBlockParagraph,
    LazyNimbusContentBlockCode,
    LazyNimbusContentBlockCard
} from '#components'

const props = defineProps<ContentBlockRendererProps>()

// Drop empty spacer text nodes (e.g. the boundary nodes the legacy CMS inserted around
// inline links) so they don't render as stray <br> elements. A genuinely empty
// paragraph — a block whose only child is an empty text node — is preserved.
const renderableContent = computed(() => filterRenderableBlocks(props.content))

const COMPONENTS: Record<ContentBlockType, ReturnType<typeof defineAsyncComponent>> = {
    'text': LazyNimbusContentBlockText,
    'link': LazyNimbusContentBlockLink,
    'list': LazyNimbusContentBlockList,
    'list-item': LazyNimbusContentBlockListItem,
    'heading': LazyNimbusContentBlockHeading,
    'paragraph': LazyNimbusContentBlockParagraph,
    'code': LazyNimbusContentBlockCode,
    'card': LazyNimbusContentBlockCard,
}

// Components that support the newTabLinks prop
const SUPPORTS_NEW_TAB_LINKS = ['link', 'paragraph', 'code', 'card']
</script>

<template>
    <template v-for="(child, childIndex) in renderableContent" :key="childIndex">
        <component
            :is="COMPONENTS[child.type]"
            v-bind="{
                ...child,
                ...(SUPPORTS_NEW_TAB_LINKS.includes(child.type) ? { newTabLinks: props?.newTabLinks } : {})
            }"
        />
    </template>
</template>
